import Emitter from 'tiny-emitter'
import moment from 'moment'

class Auth {
  #me = null
  #token = null
  #tokenType = null
  #expiresIn = null
  #expiresTime = null
  #expiresOffset = 1800 // 提前半小时过期
  #ready // 数据准备
  #loggingIn = false // 正在登录中
  /**
   * 事件：
   * login：登录后
   * logout：退出登录后
   * refresh：刷新认证后
   * me：载入用户信息后
   * @type {TinyEmitter}
   */
  event = new Emitter.TinyEmitter()
  #autoRefreshTimeoutId = null

  constructor () {
    this.ready()
  }

  get me () {
    if (!this.#me) {
      return null
    }
    return this.#me
  }

  get token () {
    if (!this.#token) {
      this.#token = localStorage.getItem('token')
    }
    return this.#token
  }

  get tokenType () {
    if (!this.#tokenType) {
      this.#tokenType = localStorage.getItem('tokenType')
    }
    return this.#tokenType
  }

  get expiresTime () {
    if (!this.#expiresTime) {
      this.#expiresTime = localStorage.getItem('expiresTime')
    }
    return this.#expiresTime
  }

  get headers () {
    const headers = {}
    if (this.isLogged() && this.#loggingIn === false) {
      headers.Authorization = (this.tokenType ? this.tokenType + ' ' : '') + this.token
    }
    return headers
  }

  /**
   * 请在数据准备完成后再执行后续操作
   * 通常在进入需要认证路由前调用
   *
   * @returns {*}
   */
  ready () {
    if (this.#ready) {
      return this.#ready
    }
    this.#ready = new Promise((resolve) => {
      if (this.isLogged()) {
        // 过期直接退出登录
        if (this.isExpired()) {
          // 过期登出
          return this.logout().then(resolve)
        } else {
          this._autoRefresh()
          // 获得用户数据
          return this._meApi()
            .then((me) => this._saveMe(me))
            .finally(resolve)
        }
      }
      return resolve()
    })
    return this.#ready
  }

  /**
   * 登入
   * @private
   */
  async _loginApi (params) {
  }

  /**
   * 登出
   * @private
   */
  async _logoutApi () {
  }

  /**
   * 登出
   * @private
   */
  async _meApi () {
  }

  /**
   * 刷新Token
   * @private
   */
  async _refreshApi () {
  }

  /**
   * 是否过期的
   */
  isExpired () {
    return moment().unix() > (this.expiresTime - this.#expiresOffset)
  }

  /**
   * 判断是否已登录
   */
  isLogged () {
    return this.token && this.expiresTime
  }

  /**
   * 登入
   */
  async login (params) {
    this.#loggingIn = true
    try {
      const {
        token,
        tokenType,
        expiresIn,
      } = await this._loginApi(params)
      this._saveLogin(token, tokenType, expiresIn)
    } finally {
      this.#loggingIn = false
    }
    this._saveMe(await this._meApi())
    // 自动刷新token
    this._autoRefresh()
    // 触发事件
    this.event.emit('login')
  }

  /**
   * 登出
   */
  async logout () {
    try {
      // 仅发送退出登录请求，如果退出登录失败也继续执行
      await this._logoutApi().then()
    } catch (e) {
    }
    this._clearLogin()
    this._clearMe()
    // 触发事件
    this.event.emit('logout')
  }

  /**
   * 刷新Token
   */
  async refresh () {
    const {
      token,
      tokenType,
      expiresIn,
    } = await this._refreshApi()
    this._saveLogin(token, tokenType, expiresIn)
    const me = await this._meApi()
    this._saveMe(me)
    // 自动刷新token
    this._autoRefresh()
    // 触发事件
    this.event.emit('refresh')
  }

  /**
   * 保存登录
   * @param token
   * @param expiresIn
   * @private
   */
  _saveLogin (token, tokenType, expiresIn) {
    this.#token = token
    this.#tokenType = tokenType
    this.#expiresIn = expiresIn
    this.#expiresTime = moment().unix() + expiresIn
    localStorage.setItem('token', this.#token)
    localStorage.setItem('tokenType', this.#tokenType)
    localStorage.setItem('expiresIn', this.#expiresIn)
    localStorage.setItem('expiresTime', this.#expiresTime)
  }

  /**
   * 删除登录
   * @private
   */
  _clearLogin () {
    this.#token = null
    this.#tokenType = null
    this.#expiresIn = null
    this.#expiresTime = null
    localStorage.removeItem('token')
    localStorage.removeItem('tokenType')
    localStorage.removeItem('expiresIn')
    localStorage.removeItem('expiresTime')
  }

  /**
   * 保存登录用户信息
   * @param me
   * @private
   */
  _saveMe (me) {
    this.#me = me
    this.event.emit('me', this.#me)
  }

  /**
   * 删除用户信息
   * @private
   */
  _clearMe () {
    localStorage.removeItem('me')
    this.#me = null
  }

  /**
   * 自动刷新token
   * @private
   */
  _autoRefresh () {
    if (this.#autoRefreshTimeoutId) {
      clearInterval(this.#autoRefreshTimeoutId)
    }
    if (this.expiresTime) {
      this.#autoRefreshTimeoutId = setTimeout(
        () => this.refresh(),
        (this.expiresTime - moment().unix() - this.#expiresOffset) * 1000,
      )
    }
  }
}

export default Auth
